import React, { useState, useEffect } from "react";
import {
  addOrganizationMember,
  updateOrganizationMemberRole,
  removeOrganizationMember,
  resendMemberInvitation,
} from "../../../utils/Api";
import { Mail, MoreVertical, Search, UserPlus } from "lucide-react";
import AddMemberModal from "../../../modals/AddMemberModal";
import EditRoleModal from "../../../modals/EditRoleModal";
import InvitationUrlModal from "../../../modals/InvitationUrlModal";
import RemoveMemberModal from "../../../modals/RemoveMemberModal";
import { useAuth } from "../../../utils/AuthContext";
import { useSendEmail } from "../../../hooks/useSendEmail";
import {
  memberRemovalNotificationEmail,
  organizationInvitationEmail,
} from "../../../emails";

export interface Member {
  id: string;
  user_id: string | null;
  db_user_id: string | null;
  email: string;
  name?: string;
  role: string;
  status: string;
  created_at: string;
}

interface MembersProps {
  organizationId: string;
  organizationName: string;
  userRole: string;
  members: Member[];
  onMembersChange: () => Promise<void>;
  isAddMemberModalOpen: boolean;
  setIsAddMemberModalOpen: (open: boolean) => void;
}

const Members: React.FC<MembersProps> = ({
  organizationId,
  organizationName,
  userRole,
  members,
  onMembersChange,
  isAddMemberModalOpen,
  setIsAddMemberModalOpen,
}) => {
  const { user } = useAuth();
  const { sendEmail } = useSendEmail();
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("member");
  const [addMemberError, setAddMemberError] = useState<string | null>(null);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);
  const [editMemberRole, setEditMemberRole] = useState("");
  const [editRoleError, setEditRoleError] = useState<string | null>(null);
  const [editRoleLoading, setEditRoleLoading] = useState(false);
  const [removingMember, setRemovingMember] = useState<Member | null>(null);
  const [isRemoveMemberModalOpen, setIsRemoveMemberModalOpen] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState<string | null>(null);
  const [invitationUrl, setInvitationUrl] = useState<string | null>(null);
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (openMenuId && !target.closest(".menu-container")) {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openMenuId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "member":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const isPendingMember = (member: Member) => {
    return member.status === "invited";
  };

  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) {
      setAddMemberError("Please enter an email address");
      return;
    }

    setAddMemberLoading(true);
    setAddMemberError(null);

    try {
      const response = await addOrganizationMember(
        organizationId,
        newMemberEmail,
        newMemberRole
      );

      await onMembersChange();

      if (response.data.invitation_url) {
        await sendEmail({
          to: newMemberEmail,
          template: organizationInvitationEmail({
            organizationName,
            invitationUrl: response.data.invitation_url,
          }),
        });

        setInvitationUrl(response.data.invitation_url);
        setIsInvitationModalOpen(true);
        setIsCopied(false);
      }

      setNewMemberEmail("");
      setNewMemberRole("member");
      setIsAddMemberModalOpen(false);
    } catch (err: any) {
      console.error("Failed to add member:", err);
      setAddMemberError(err.response?.data?.detail || "Failed to add member");
    } finally {
      setAddMemberLoading(false);
    }
  };

  const handleCopyInvitationUrl = () => {
    if (invitationUrl) {
      navigator.clipboard.writeText(invitationUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleEditRole = (member: Member) => {
    setEditingMember(member);
    setEditMemberRole(member.role);
    setIsEditRoleModalOpen(true);
    setOpenMenuId(null);
  };

  const handleUpdateRole = async () => {
    if (!editingMember) return;

    setEditRoleLoading(true);
    setEditRoleError(null);

    try {
      await updateOrganizationMemberRole(
        organizationId,
        editingMember.id,
        editMemberRole
      );

      await onMembersChange();

      setIsEditRoleModalOpen(false);
      setEditingMember(null);
    } catch (err: any) {
      console.error("Failed to update member role:", err);
      setEditRoleError(
        err.response?.data?.detail || "Failed to update member role"
      );
    } finally {
      setEditRoleLoading(false);
    }
  };

  const handleRemoveMemberClick = (member: Member) => {
    setRemovingMember(member);
    setIsRemoveMemberModalOpen(true);
    setRemoveError(null);
    setOpenMenuId(null);
  };

  const handleRemoveMember = async () => {
    if (!removingMember) return;

    setRemoveLoading(true);
    setRemoveError(null);

    try {
      await removeOrganizationMember(organizationId, removingMember.id);
      await onMembersChange();

      await sendEmail({
        to: removingMember.email,
        template: memberRemovalNotificationEmail({
          organizationName,
          memberName: removingMember.name,
        }),
      });

      setIsRemoveMemberModalOpen(false);
      setRemovingMember(null);
    } catch (err: any) {
      console.error("Failed to remove member:", err);
      setRemoveError(err.response?.data?.detail || "Failed to remove member");
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleResendInvitation = async (member: Member) => {
    setResendLoading(member.id);
    setOpenMenuId(null);

    try {
      const response = await resendMemberInvitation(organizationId, member.id);

      if (response.data.invitation_url) {
        await sendEmail({
          to: member.email,
          template: organizationInvitationEmail({
            organizationName,
            invitationUrl: response.data.invitation_url,
          }),
        });

        setInvitationUrl(response.data.invitation_url);
        setIsInvitationModalOpen(true);
        setIsCopied(false);
      }
    } catch (err: any) {
      console.error("Failed to resend invitation:", err);
    } finally {
      setResendLoading(null);
    }
  };

  const filteredMembers = members.filter((member) => {
    const query = searchQuery.toLowerCase();
    const name = (member.name || "").toLowerCase();
    const email = (member.email || "").toLowerCase();
    return name.includes(query) || email.includes(query);
  });

  return (
    <>
      <div className="mb-6 pb-32">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-full sm:w-64"
            />
          </div>
          {userRole.toLowerCase() === "admin" && (
            <button
              onClick={() => setIsAddMemberModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#1d365d] text-white rounded-md hover:bg-[#16294a] transition-colors text-sm font-medium whitespace-nowrap"
            >
              <UserPlus className="w-4 h-4" />
              Add member
            </button>
          )}
        </div>
        <div className="bg-white rounded shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white border-b border-gray-200">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-gray-600">Name</th>
                <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden sm:table-cell">Email</th>
                <th className="px-3 sm:px-6 py-3 text-left text-gray-600">Role</th>
                <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden md:table-cell">Joined</th>
                {userRole.toLowerCase() === "admin" && (
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 w-16 sm:w-auto">
                    <span className="hidden sm:inline">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMembers.length === 0 ? (
                <tr>
                  <td
                    colSpan={userRole.toLowerCase() === "admin" ? 5 : 4}
                    className="px-3 sm:px-6 py-12 text-center"
                  >
                    <div className="text-gray-500">
                      <Mail className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <h4 className="text-lg font-semibold mb-2">
                        {members.length === 0
                          ? "No members yet"
                          : "No members found"}
                      </h4>
                      <p>
                        {members.length === 0
                          ? "Add members to start collaborating"
                          : "Try a different search query"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr
                    key={member.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {!member.user_id
                              ? "?"
                              : (member.name || member.email || "?")
                                  .charAt(0)
                                  .toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {!member.user_id ? "Pending member" : member.name || "—"}
                          </div>
                          <div className="text-xs text-gray-500 sm:hidden truncate max-w-[120px]">
                            {member.email || "Hidden"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 hidden sm:table-cell">
                      {member.email || <span className="text-gray-400">Hidden</span>}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(
                            member.role
                          )}`}
                        >
                          {member.role}
                        </span>
                        {isPendingMember(member) && (
                          <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 hidden md:table-cell">
                      {formatDate(member.created_at)}
                    </td>
                    {userRole.toLowerCase() === "admin" && (
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="relative menu-container">
                          <button
                            onClick={(e) => {
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              const spaceBelow = window.innerHeight - rect.bottom;
                              const menuHeight = 100;
                              if (openMenuId === member.id) {
                                setOpenMenuId(null);
                                setMenuPosition(null);
                              } else {
                                setMenuPosition(
                                  spaceBelow < menuHeight
                                    ? { top: rect.top - menuHeight, right: window.innerWidth - rect.right }
                                    : { top: rect.bottom + 4, right: window.innerWidth - rect.right }
                                );
                                setOpenMenuId(member.id);
                              }
                            }}
                            disabled={
                              (member.email && member.email === user?.email) ||
                              (removeLoading &&
                                removingMember?.id === member.id) ||
                              resendLoading === member.id
                            }
                            className="p-1 hover:bg-gray-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            type="button"
                          >
                            <MoreVertical className="w-5 h-5 text-gray-600" />
                          </button>
                          {openMenuId === member.id && menuPosition && (
                            <div
                              className="fixed w-48 bg-white rounded-lg shadow-xl z-50 border border-gray-200 overflow-hidden"
                              style={{
                                top: menuPosition.top,
                                right: menuPosition.right,
                                boxShadow:
                                  "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                              }}
                            >
                              <div className="py-1">
                                {isPendingMember(member) ? (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleResendInvitation(member);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                    type="button"
                                  >
                                    <span>Resend Invitation</span>
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleEditRole(member);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                    type="button"
                                  >
                                    <span>Edit Access</span>
                                  </button>
                                )}
                                <div className="border-t border-gray-100"></div>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleRemoveMemberClick(member);
                                  }}
                                  className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                  type="button"
                                >
                                  <span>Remove Member</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {members.length > 0 && (
            <div className="px-3 sm:px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">
                {searchQuery ? (
                  <>
                    Showing{" "}
                    <span className="font-medium">{filteredMembers.length}</span>{" "}
                    of <span className="font-medium">{members.length}</span>{" "}
                    members
                  </>
                ) : (
                  <>
                    Total members:{" "}
                    <span className="font-medium">{members.length}</span>
                  </>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      <AddMemberModal
        isOpen={isAddMemberModalOpen}
        setIsOpen={setIsAddMemberModalOpen}
        newMemberEmail={newMemberEmail}
        setNewMemberEmail={setNewMemberEmail}
        newMemberRole={newMemberRole}
        setNewMemberRole={setNewMemberRole}
        addMemberError={addMemberError}
        addMemberLoading={addMemberLoading}
        onAddMember={handleAddMember}
      />

      <EditRoleModal
        isOpen={isEditRoleModalOpen}
        setIsOpen={setIsEditRoleModalOpen}
        editingMember={editingMember}
        editMemberRole={editMemberRole}
        setEditMemberRole={setEditMemberRole}
        editRoleError={editRoleError}
        editRoleLoading={editRoleLoading}
        onUpdateRole={handleUpdateRole}
      />

      <InvitationUrlModal
        isOpen={isInvitationModalOpen}
        setIsOpen={setIsInvitationModalOpen}
        invitationUrl={invitationUrl}
        isCopied={isCopied}
        onCopyInvitationUrl={handleCopyInvitationUrl}
      />

      <RemoveMemberModal
        isOpen={isRemoveMemberModalOpen}
        setIsOpen={setIsRemoveMemberModalOpen}
        removingMember={removingMember}
        removeLoading={removeLoading}
        removeError={removeError}
        onRemoveMember={handleRemoveMember}
      />
    </>
  );
};

export default Members;
